import { clamp } from './geometry.js';
import { C, L, M, Q, Z, poly, type Path } from './path.js';
import type { Rect, ShapeKind, Vec } from './types.js';

/** Bezier circle constant: how far control points sit along the tangent. */
const KAPPA = 0.5522847498307936;

function unit(points: readonly [number, number][], r: Rect): Vec[] {
  return points.map(([nx, ny]) => ({ x: r.x + nx * r.w, y: r.y + ny * r.h }));
}

/** Regular n-gon inscribed in the box, first vertex pointing up, then stretched to fill. */
function regular(sides: number, r: Rect, rotate = -Math.PI / 2): Vec[] {
  const pts: [number, number][] = [];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < sides; i++) {
    const a = rotate + (i * Math.PI * 2) / sides;
    const x = Math.cos(a);
    const y = Math.sin(a);
    pts.push([x, y]);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const sx = maxX - minX;
  const sy = maxY - minY;
  return pts.map(([x, y]) => ({
    x: r.x + ((x - minX) / sx) * r.w,
    y: r.y + ((y - minY) / sy) * r.h,
  }));
}

function star(points: number, innerRatio: number, r: Rect): Vec[] {
  const raw: [number, number][] = [];
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 === 0 ? 1 : innerRatio;
    const a = -Math.PI / 2 + (i * Math.PI) / points;
    raw.push([Math.cos(a) * rad, Math.sin(a) * rad]);
  }
  // Normalise so the outer vertices touch the box.
  return raw.map(([x, y]) => ({
    x: r.x + ((x + 1) / 2) * r.w,
    y: r.y + ((y + 1) / 2) * r.h,
  }));
}

function ellipsePath(r: Rect): Path {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const rx = r.w / 2;
  const ry = r.h / 2;
  const ox = rx * KAPPA;
  const oy = ry * KAPPA;
  return [
    M(cx, cy - ry),
    C(cx + ox, cy - ry, cx + rx, cy - oy, cx + rx, cy),
    C(cx + rx, cy + oy, cx + ox, cy + ry, cx, cy + ry),
    C(cx - ox, cy + ry, cx - rx, cy + oy, cx - rx, cy),
    C(cx - rx, cy - oy, cx - ox, cy - ry, cx, cy - ry),
    Z,
  ];
}

function roundRectPath(r: Rect, radius: number): Path {
  const rad = clamp(radius, 0, Math.min(r.w, r.h) / 2);
  if (rad <= 0.01) return poly(unit([[0, 0], [1, 0], [1, 1], [0, 1]], r));
  const { x, y, w, h } = r;
  return [
    M(x + rad, y),
    L(x + w - rad, y),
    Q(x + w, y, x + w, y + rad),
    L(x + w, y + h - rad),
    Q(x + w, y + h, x + w - rad, y + h),
    L(x + rad, y + h),
    Q(x, y + h, x, y + h - rad),
    L(x, y + rad),
    Q(x, y, x + rad, y),
    Z,
  ];
}

/**
 * Half-ellipse arc, assuming the cursor already sits on the left end
 * (cx - rx, cy). `dir` is +1 to bulge downwards, -1 upwards.
 */
function halfEllipse(cx: number, cy: number, rx: number, ry: number, dir: 1 | -1): Path {
  const ox = rx * KAPPA;
  const oy = ry * KAPPA * dir;
  return [
    C(cx - rx, cy + oy, cx - ox, cy + ry * dir, cx, cy + ry * dir),
    C(cx + ox, cy + ry * dir, cx + rx, cy + oy, cx + rx, cy),
  ];
}

/** Same arc traversed right to left, so subpaths can be chained without a moveTo. */
function halfEllipseRev(cx: number, cy: number, rx: number, ry: number, dir: 1 | -1): Path {
  const ox = rx * KAPPA;
  const oy = ry * KAPPA * dir;
  return [
    C(cx + rx, cy + oy, cx + ox, cy + ry * dir, cx, cy + ry * dir),
    C(cx - ox, cy + ry * dir, cx - rx, cy + oy, cx - rx, cy),
  ];
}

function cylinderPath(r: Rect): Path {
  const ry = Math.max(Math.min(r.h * 0.14, r.h / 2 - 0.5), 0.5);
  const rx = r.w / 2;
  const cx = r.x + rx;
  const top = r.y + ry;
  const bottom = r.y + r.h - ry;
  const body: Path = [
    M(r.x, top),
    ...halfEllipse(cx, top, rx, ry, -1),
    L(r.x + r.w, bottom),
    ...halfEllipseRev(cx, bottom, rx, ry, 1),
    Z,
  ];
  // Second subpath draws the visible rim of the top face.
  const rim: Path = [M(r.x, top), ...halfEllipse(cx, top, rx, ry, 1)];
  return [...body, ...rim];
}

function cloudPath(r: Rect): Path {
  const cmds: [number, number, number, number, number, number][] = [
    [0.07, 0.78, 0.0, 0.68, 0.04, 0.58],
    [0.07, 0.5, 0.14, 0.46, 0.21, 0.47],
    [0.21, 0.33, 0.32, 0.22, 0.45, 0.22],
    [0.55, 0.22, 0.64, 0.28, 0.68, 0.37],
    [0.75, 0.31, 0.86, 0.32, 0.92, 0.4],
    [0.98, 0.48, 0.98, 0.6, 0.92, 0.68],
    [0.97, 0.74, 0.94, 0.78, 0.86, 0.78],
  ];
  const path: Path = [M(r.x + 0.18 * r.w, r.y + 0.78 * r.h)];
  for (const [a, b, c, d, e, f] of cmds) {
    path.push(
      C(r.x + a * r.w, r.y + b * r.h, r.x + c * r.w, r.y + d * r.h, r.x + e * r.w, r.y + f * r.h),
    );
  }
  path.push(Z);
  return path;
}

function speechBubblePath(r: Rect): Path {
  const bodyH = r.h * 0.8;
  const body: Rect = { x: r.x, y: r.y, w: r.w, h: bodyH };
  const rad = clamp(Math.min(r.w, bodyH) * 0.14, 2, 24);
  const base = roundRectPath(body, rad);
  // Replace the closing segment with a tail dropping from the lower left.
  const tail: Path = [
    M(r.x + r.w * 0.22, r.y + bodyH),
    L(r.x + r.w * 0.18, r.y + r.h),
    L(r.x + r.w * 0.42, r.y + bodyH),
    Z,
  ];
  return [...base, ...tail];
}

function heartPath(r: Rect): Path {
  const p = (nx: number, ny: number): [number, number] => [r.x + nx * r.w, r.y + ny * r.h];
  const [sx, sy] = p(0.5, 0.98);
  const seg: [number, number, number, number, number, number][] = [
    [0.42, 0.86, 0.02, 0.62, 0.02, 0.34],
    [0.02, 0.12, 0.2, 0.02, 0.34, 0.06],
    [0.42, 0.09, 0.48, 0.15, 0.5, 0.2],
    [0.52, 0.15, 0.58, 0.09, 0.66, 0.06],
    [0.8, 0.02, 0.98, 0.12, 0.98, 0.34],
    [0.98, 0.62, 0.58, 0.86, 0.5, 0.98],
  ];
  const path: Path = [M(sx, sy)];
  for (const [a, b, c, d, e, f] of seg) {
    const [c1x, c1y] = p(a, b);
    const [c2x, c2y] = p(c, d);
    const [ex, ey] = p(e, f);
    path.push(C(c1x, c1y, c2x, c2y, ex, ey));
  }
  path.push(Z);
  return path;
}

function halfCirclePath(r: Rect): Path {
  const rx = r.w / 2;
  const cx = r.x + rx;
  const ry = r.h;
  const baseY = r.y + r.h;
  return [M(r.x, baseY), ...halfEllipse(cx, baseY, rx, ry, -1), Z];
}

function crossPath(r: Rect): Path {
  const t = 1 / 3;
  return poly(
    unit(
      [
        [t, 0],
        [1 - t, 0],
        [1 - t, t],
        [1, t],
        [1, 1 - t],
        [1 - t, 1 - t],
        [1 - t, 1],
        [t, 1],
        [t, 1 - t],
        [0, 1 - t],
        [0, t],
        [t, t],
      ],
      r,
    ),
  );
}

/**
 * Geometry for a shape kind, in board coordinates, before rotation.
 * `radius` only affects the rectangular kinds.
 */
export function shapePath(kind: ShapeKind, r: Rect, radius = 0): Path {
  switch (kind) {
    case 'rect':
      return poly(unit([[0, 0], [1, 0], [1, 1], [0, 1]], r));
    case 'roundRect':
      return roundRectPath(r, radius > 0 ? radius : Math.min(r.w, r.h) * 0.12);
    case 'ellipse':
      return ellipsePath(r);
    case 'triangle':
      return poly(unit([[0.5, 0], [1, 1], [0, 1]], r));
    case 'rightTriangle':
      return poly(unit([[0, 0], [0, 1], [1, 1]], r));
    case 'diamond':
      return poly(unit([[0.5, 0], [1, 0.5], [0.5, 1], [0, 0.5]], r));
    case 'pentagon':
      return poly(regular(5, r));
    case 'hexagon':
      return poly(unit([[0.25, 0], [0.75, 0], [1, 0.5], [0.75, 1], [0.25, 1], [0, 0.5]], r));
    case 'octagon':
      return poly(regular(8, r, -Math.PI / 2 - Math.PI / 8));
    case 'star':
      return poly(star(5, 0.382, r));
    case 'parallelogram':
      return poly(unit([[0.25, 0], [1, 0], [0.75, 1], [0, 1]], r));
    case 'trapezoid':
      return poly(unit([[0.25, 0], [0.75, 0], [1, 1], [0, 1]], r));
    case 'chevron':
      return poly(unit([[0, 0], [0.75, 0], [1, 0.5], [0.75, 1], [0, 1], [0.25, 0.5]], r));
    case 'arrowRight':
      return poly(
        unit([[0, 0.3], [0.65, 0.3], [0.65, 0], [1, 0.5], [0.65, 1], [0.65, 0.7], [0, 0.7]], r),
      );
    case 'arrowLeft':
      return poly(
        unit([[1, 0.3], [0.35, 0.3], [0.35, 0], [0, 0.5], [0.35, 1], [0.35, 0.7], [1, 0.7]], r),
      );
    case 'cross':
      return crossPath(r);
    case 'cylinder':
      return cylinderPath(r);
    case 'cloud':
      return cloudPath(r);
    case 'speechBubble':
      return speechBubblePath(r);
    case 'heart':
      return heartPath(r);
    case 'halfCircle':
      return halfCirclePath(r);
  }
}

/**
 * Fraction of the box that is safe for text. Wedge-shaped geometry gets a
 * tighter box so labels do not spill outside the outline.
 */
export function shapeTextInset(kind: ShapeKind): {
  top: number;
  right: number;
  bottom: number;
  left: number;
} {
  switch (kind) {
    case 'triangle':
      return { top: 0.42, right: 0.14, bottom: 0.06, left: 0.14 };
    case 'rightTriangle':
      return { top: 0.42, right: 0.06, bottom: 0.06, left: 0.14 };
    case 'diamond':
      return { top: 0.22, right: 0.22, bottom: 0.22, left: 0.22 };
    case 'ellipse':
    case 'pentagon':
    case 'octagon':
      return { top: 0.14, right: 0.14, bottom: 0.14, left: 0.14 };
    case 'star':
      return { top: 0.34, right: 0.24, bottom: 0.22, left: 0.24 };
    case 'hexagon':
    case 'chevron':
      return { top: 0.08, right: 0.2, bottom: 0.08, left: 0.2 };
    case 'arrowRight':
      return { top: 0.32, right: 0.36, bottom: 0.32, left: 0.06 };
    case 'arrowLeft':
      return { top: 0.32, right: 0.06, bottom: 0.32, left: 0.36 };
    case 'trapezoid':
    case 'parallelogram':
      return { top: 0.08, right: 0.2, bottom: 0.08, left: 0.2 };
    case 'cylinder':
      return { top: 0.2, right: 0.08, bottom: 0.2, left: 0.08 };
    case 'cloud':
      return { top: 0.3, right: 0.16, bottom: 0.26, left: 0.16 };
    case 'speechBubble':
      return { top: 0.08, right: 0.1, bottom: 0.28, left: 0.1 };
    case 'heart':
      return { top: 0.24, right: 0.18, bottom: 0.3, left: 0.18 };
    case 'halfCircle':
      return { top: 0.35, right: 0.16, bottom: 0.06, left: 0.16 };
    case 'cross':
      return { top: 0.34, right: 0.34, bottom: 0.34, left: 0.34 };
    default:
      return { top: 0.05, right: 0.06, bottom: 0.05, left: 0.06 };
  }
}

export function shapeTextRect(kind: ShapeKind, r: Rect): Rect {
  const i = shapeTextInset(kind);
  return {
    x: r.x + r.w * i.left,
    y: r.y + r.h * i.top,
    w: r.w * (1 - i.left - i.right),
    h: r.h * (1 - i.top - i.bottom),
  };
}

export const SHAPE_KINDS: ShapeKind[] = [
  'rect',
  'roundRect',
  'ellipse',
  'triangle',
  'rightTriangle',
  'diamond',
  'pentagon',
  'hexagon',
  'octagon',
  'star',
  'parallelogram',
  'trapezoid',
  'chevron',
  'arrowRight',
  'arrowLeft',
  'cross',
  'cylinder',
  'cloud',
  'speechBubble',
  'heart',
  'halfCircle',
];

export const SHAPE_LABELS: Record<ShapeKind, string> = {
  rect: 'Прямоугольник',
  roundRect: 'Прямоугольник со скруглением',
  ellipse: 'Эллипс',
  triangle: 'Треугольник',
  rightTriangle: 'Прямоугольный треугольник',
  diamond: 'Ромб',
  pentagon: 'Пятиугольник',
  hexagon: 'Шестиугольник',
  octagon: 'Восьмиугольник',
  star: 'Звезда',
  parallelogram: 'Параллелограмм',
  trapezoid: 'Трапеция',
  chevron: 'Шеврон',
  arrowRight: 'Стрелка вправо',
  arrowLeft: 'Стрелка влево',
  cross: 'Крест',
  cylinder: 'Цилиндр',
  cloud: 'Облако',
  speechBubble: 'Речевое облако',
  heart: 'Сердце',
  halfCircle: 'Полукруг',
};
